/**
 * sendChain — serialised WebSocket binary-send pipeline for mic chunks.
 *
 * The bug it prevents (#131, originally fixed in 88faf16 and 6c754f2):
 * MediaRecorder's `ondataavailable` handler used to do
 *
 *   e.data.arrayBuffer().then(ab => ws.send(ab))
 *
 * directly. The `.then()` is asynchronous, so the final chunk's send could
 * resolve AFTER the synchronous `audio_end` send in `onstop`. The server
 * then saw `audio_end` first and treated the chunks as stray "Stray binary
 * blob mid-conversation" — STT got nothing, the user appeared to be muted.
 *
 * This module solves it by serialising every send onto a single promise
 * chain. `onstop` can `await chain.drain()` and be sure all enqueued sends
 * landed on the wire before sending `audio_end`.
 *
 * Usage:
 *   const chain = createSendChain({
 *     getWs: () => wsRef.current,
 *     isSessionActive: () => sessionActiveRef.current,
 *   });
 *   // for each MediaRecorder chunk:
 *   chain.enqueue(blob);
 *   // when stopping:
 *   await chain.drain();
 *   ws.send(JSON.stringify({ type: 'audio_end' }));
 */
export function createSendChain({ getWs, isSessionActive }) {
  let chain = Promise.resolve();

  function enqueue(blob) {
    chain = chain
      .then(async () => {
        const ws = getWs();
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        if (!isSessionActive()) return;
        const ab = await blob.arrayBuffer();
        // Re-check after the await — the WS may have closed or the session
        // may have been killed (killMic) while we were waiting on the
        // Blob → ArrayBuffer conversion.
        const ws2 = getWs();
        if (ws2 && ws2.readyState === WebSocket.OPEN && isSessionActive()) {
          ws2.send(ab);
        }
      })
      .catch((err) => {
        // Never let one bad chunk break the chain — log and continue.
        // eslint-disable-next-line no-console
        console.warn('[sendChain] error:', err);
      });
  }

  function drain() {
    // Returns the current tail of the chain. Anything enqueued AFTER drain()
    // is called won't be awaited — but in practice the caller gates new
    // enqueues by the same lifecycle that triggered the drain (mic stopped).
    return chain;
  }

  function reset() {
    chain = Promise.resolve();
  }

  return { enqueue, drain, reset };
}
